
import React, { useState, useMemo, useEffect, createContext } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Box, ThemeProvider, CssBaseline } from "@mui/material";
import { getCustomTheme } from "./theme";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import NetworthPromoCard from "./components/NetworthPromoCard";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Blogs from "./pages/Blogs";
import BlogDetail from "./pages/BlogDetail";
import Tools from "./pages/Tools";
import ToolDetail from "./pages/ToolDetail";
import RentAgreementDetector from "./pages/RentAgreementDetector";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Custom404 from "./pages/404";
import Custom500 from "./pages/500";

export const ColorModeContext = createContext({ toggleColorMode: () => {}, mode: 'light' });

function App() {
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('theme-mode');
    return savedMode === 'dark' ? 'dark' : 'light';
  });

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const nextMode = prevMode === 'light' ? 'dark' : 'light';
          localStorage.setItem('theme-mode', nextMode);
          return nextMode;
        });
      },
      mode,
    }),
    [mode]
  );

  const theme = useMemo(() => getCustomTheme(mode), [mode]);

  useEffect(() => {
    if (mode === 'dark') {
      document.body.classList.add('dark-theme-active');
    } else {
      document.body.classList.remove('dark-theme-active');
    }
  }, [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <ScrollToTop />
          <Box 
            className="site-shell dot-grid-bg" 
            sx={{ 
              display: "flex", 
              flexDirection: "column", 
              minHeight: "100vh",
              bgcolor: 'background.default',
              color: 'text.primary',
              transition: 'background-color 0.3s ease, color 0.3s ease'
            }}
          >
            <Navbar />
            <Box component="main" sx={{ flexGrow: 1, position: 'relative', zIndex: 1 }}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/blogs" element={<Blogs />} />
                <Route path="/blogs/:slug" element={<BlogDetail />} />
                <Route path="/tools" element={<Tools />} />
                <Route path="/tools/rent-agreement-detector" element={<RentAgreementDetector />} />
                <Route path="/tools/:slug" element={<ToolDetail />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/404" element={<Custom404 />} />
                <Route path="/500" element={<Custom500 />} />
                <Route path="*" element={<Custom404 />} />
              </Routes>
            </Box>
            <Footer />
            <NetworthPromoCard />
          </Box>
        </Router>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
